import globalStore from '$$globalStore';
import app from '$$app';
import constants from '$$helpers/constants';
import projectsModel from '$$projectModel';
import { closeTask } from '../helpers';
import rights from '../../../components/rights';
import TreeWorker from '../../../components/workers/TreeWorker';
import userCustomColumnModule from '../../../components/userCustomColumns';

const TaskSettingsController = {

  appEventsListeners: [],

  getUpdateMethodByProperty(property) {
    const UPDATE_PROPERTY_METHODS_MAP = {
      type: this._updateType.bind(this),
      start_date: this._updateStartDate.bind(this),
      end_date: this._updateEndDate.bind(this),
      duration: this._updateDuration.bind(this),
      estimation: this._updateEstimation.bind(this),
      deadline: this._updateDeadline.bind(this),
      note: this._updateDescription.bind(this),
      priority: this._updatePriority.bind(this),
      status: this._updateStatus.bind(this),
      progress: this._updateProgress.bind(this),
      text: this._updateTaskName.bind(this),
      color: this._updateColor.bind(this),
    };

    if (!UPDATE_PROPERTY_METHODS_MAP[property]) {
      throw Error(`Update method does not exist for property ${property}`);
    }

    return UPDATE_PROPERTY_METHODS_MAP[property];
  },

  async getTasksWbsCodes(ganttIds) {
    const projectGanttTasksDataMap = ganttIds.reduce((result, ganttId) => {
      result[ganttId] = globalStore.getters['tasksModel/getTasksForGantt'](ganttId);

      return result;
    }, {});

    const data = await TreeWorker.calculate(projectGanttTasksDataMap);

    return data;
  },

  setTaskData(taskData) {
    this.taskData = { ...taskData };
  },

  setCustomFields(columns, values) {
    this.customFieldsColumns = columns;
    this.customFieldsValues = values;
  },

  async updateTaskDataPartially({ property, payload }) {
    if (this.getUpdateMethodByProperty(property)) {
      await this.getUpdateMethodByProperty(property)(payload);
    }

    const tasksToUpdate = [{ ...this.taskData }];

    await globalStore.dispatch('tasksModel/updateTasks', {
      tasks: tasksToUpdate,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_UPDATE,
        sourceId: this.taskData.id,
      },
      fromGantt: false,
    });
  },

  updateCustomField(column, value) {
    const columnInModel = globalStore.getters['columns/getCustomColumnById'](column.id);

    if (!columnInModel) {
      webix.message({ type: 'warning', text: __('task_settings_custom_column_deleted') });

      return;
    }

    const type = Object.entries(constants.CUSTOM_COLUMNS_TYPES).find(([_, val]) => val.id === column.type)?.[0];

    userExtAnalytics.log('task_settings_action', { action: 'custom_field', type });
    userCustomColumnModule.changeUserCustomValue(this.taskData, column.id, value);
  },

  async updateDependencyLag(dependency, value) {
    const linksToUpdate = [{ id: dependency.linkId, lag: value }];

    await globalStore.dispatch('tasksModel/updateLinks', {
      links: linksToUpdate,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.LINK_UPDATE,
        sourceId: dependency.linkId,
      },
      fromGantt: false,
    });
  },

  checkByLinkDependency(taskData, columnName) {
    const projectConfig = projectsModel.getProjectConfig(taskData.gantt_id);

    if (columnName === 'create_date' || columnName === 'creator' || taskData.type === constants.TASK_TYPES.button) {
      return true;
    }

    if (!(columnName === 'start_date' || columnName === 'end_date') || !projectConfig.auto_scheduling) {
      return false;
    }

    const mastersData = globalStore.getters['tasksModel/getTargetLinksByGanttIdAndTaskId'](taskData.gantt_id, taskData.id);
    let shouldDisable = false;
    let alreadyBlockedByStart = false;

    mastersData.forEach(masterData => {
      switch (masterData.type) {
      case constants.GANTT_LINK_TYPES.finish_to_start:
      case constants.GANTT_LINK_TYPES.start_to_start:
        if (columnName === 'start_date') {
          shouldDisable = true;
        }
        if (columnName === 'end_date') {
          alreadyBlockedByStart = true;
          shouldDisable = false;
        }
        break;
      case constants.GANTT_LINK_TYPES.finish_to_finish:
      case constants.GANTT_LINK_TYPES.start_to_finish:
        if (columnName === 'end_date' && !alreadyBlockedByStart) {
          shouldDisable = true;
        }
        break;
      }
    });

    return shouldDisable;
  },

  _onAfterCollaborationHandler(collaborationData) {
    if (collaborationData.event === 'TaskDeleted') {
      return this._onTaskDeleted(collaborationData);
    }

    if (collaborationData.event === 'TaskUpdated') {
      // sync controller taskData with store
      if (collaborationData.tasks?.includes(this.taskData.id)) {
        this.taskData = globalStore.getters['tasksModel/getTask'](this.taskData.id);
      }
      this._onTaskUpdated(collaborationData);
    }

    if (collaborationData.event === 'ProjectDeleted') {
      this._onProjectDeleted(collaborationData.projects);
    }

    if (collaborationData.event === 'ProjectArchived') {
      if (collaborationData.projects.includes(this.taskData.gantt_id)) {
        this._onAppProjectArchive({
          gantt_id: this.taskData.gantt_id,
          is_archived: true,
        });
      }
    }

    if (collaborationData.event === 'TaskCustomFieldUpdated') {
      this._onTaskCustomFieldUpdated(collaborationData);
    }

    if (collaborationData.event === 'CustomFieldDeleted') {
      this._onTaskCustomFieldDeleted(collaborationData.customFields);
    }

    if (collaborationData.event === 'CustomFieldFromProjectUnassigned') {
      this._onCustomFieldFromProjectUnassigned(collaborationData.unassignedCustomFields);
    }

    if (collaborationData.event === 'CustomFieldToProjectAssigned') {
      this._onCustomFieldToProjectAssigned(collaborationData.assignedCustomFields);
    }

    if (collaborationData.event === 'TaskResourceUnassigned') {
      this._onTaskResourceUnassigned(collaborationData.unassignedResources);
    }
  },

  addAppEventsListeners() {
    this.removeAppEventsListeners();
    const closeTaskEvents = ['reloadModal:show', 'closeWindowOnEscKeyPressed', 'taskView:close'];

    closeTaskEvents.forEach(eventName => addAppEvent(eventName, closeTask, this));
    addAppEvent('project:archive', this._onAppProjectArchive, this);
    addAppEvent('onAfterCollaboration', this._onAfterCollaborationHandler, this);

    function addAppEvent(eventName, eventHandler, context) {
      const e_id = app.on(eventName, eventHandler.bind(context));

      context.appEventsListeners.push(e_id);
    }
  },

  removeAppEventsListeners() {
    this.appEventsListeners.forEach(id => app.off(id));
    this.appEventsListeners = [];
  },

  _onTaskResourceUnassigned(unassignedResources) {
    if (!rights.project.hasRight(this.taskData.gantt_id, 'all_tasks')) {
      const realResource = globalStore.getters['resourcesModel/getResourceByUserId'](window.user.id);
      const currentTaskUnassignedResources = unassignedResources.find(item => item.taskId === this.taskData.id);
      const isUnassignedFromCurrentTask = currentTaskUnassignedResources?.unassignedResourcesIds.includes(realResource.id);
      const projectTasksMap = globalStore.getters['tasksModel/getProjectTasksMap'](this.taskData.gantt_id);
      let isCurrentTaskIsParentOfUnassignedTask = false;
      let isAnyChildrenTaskWithCurrentUserResourceLeft = false;

      if (this.taskData.type === constants.TASK_TYPES.project) {
        isCurrentTaskIsParentOfUnassignedTask = unassignedResources.some(unassignedResource => (
          this._isTaskChildOf(unassignedResource.taskId, this.taskData.id, projectTasksMap)
          && unassignedResource.unassignedResourcesIds.includes(realResource.id)
        ));
        const taskChildren = globalStore.getters['tasksModel/getAllChildByTask'](this.taskData.gantt_id, this.taskData.id);
        // Checking if the current parent task still has children where the current resource is assigned

        isAnyChildrenTaskWithCurrentUserResourceLeft = taskChildren.some(task => (
          task.resources.find(resource => resource.resource_id === realResource.id)
        ));
      }

      if (isUnassignedFromCurrentTask || (isCurrentTaskIsParentOfUnassignedTask && !isAnyChildrenTaskWithCurrentUserResourceLeft)) {
        closeTask();
      }
    }
  },

  _onTaskUpdated(collaborationData) {
    if (!collaborationData.tasks.includes(this.taskData.id)) return;

    if (!this._isUserHasRightsAccessToCurrentTask()) {
      return closeTask();
    }
  },

  _isUserHasRightsAccessToCurrentTask() {
    if (rights.project.hasRight(this.taskData.gantt_id, 'all_tasks')) return true;

    return globalStore.getters['tasksModel/checkAccessToTask'](this.taskData.id, this.taskData.gantt_id);
  },

  _onTaskDeleted(collaborationData) {
    const isCurrentProject = collaborationData.projects?.includes(this.taskData.gantt_id);
    const isCurrentTask = collaborationData.tasks?.includes(this.taskData.id);

    if (isCurrentProject && isCurrentTask) {
      closeTask();
    }
  },

  _onProjectDeleted(projects) {
    if (projects.includes(this.taskData.gantt_id)) {
      return closeTask();
    }
  },

  _onTaskCustomFieldUpdated(collaborationData) {
    // if (!collaborationData.tasks.includes(this.taskData.id)) return;
    // collaborationData.customFields.forEach(field => {
    //   this.customFieldsValues[field.customFieldId] = Array.isArray(field.value) ? field.value.join(',') : field.value;
    // });
  },

  _onTaskCustomFieldDeleted(customFieldsIds) {
    // customFieldsIds.forEach(id => {
    //   const columnIndex = this.customFieldsColumns.findIndex(column => column.id === id);

    //   delete this.customFieldsValues[id];
    //   this.customFieldsColumns.splice(columnIndex, 1);
    // });
  },

  _onCustomFieldFromProjectUnassigned(unassignedCustomFields) {
    const customFieldsIds = unassignedCustomFields.map(field => field.customFieldId);

    this._onTaskCustomFieldDeleted(customFieldsIds);
  },

  _onCustomFieldToProjectAssigned(assignedCustomFields) {
    // if (!pricingHelper.checkPricingAccess('custom_fields')) {
    //   return;
    // }

    // assignedCustomFields.forEach(field => {
    //   const assignedColumn = globalStore.getters['columns/getCustomColumnById'](field.customFieldId);

    //   if (assignedColumn) {
    //     const fieldValue = userCustomColumnsComponent.getUserCustomValue(this.taskData, assignedColumn.id).value;

    //     this.customFieldsColumns.push(assignedColumn);
    //     this.customFieldsValues[assignedColumn.id] = fieldValue;
    //   }
    // });
  },

  _onAppProjectArchive(projectData) {
    if (projectData.gantt_id === this.taskData.gantt_id) {
      closeTask(projectData?.is_archived);
    }
  },

  _updateTaskName(value) {
    userExtAnalytics.log('task_settings_action', { action: 'name' });
    this.taskData.text = value.slice(0, 254);
  },

  _updateColor(value) {
    this.taskData.color = value;
    userExtAnalytics.log('task_settings_action', { action: 'color' });
  },

  _updatePriority(value) {
    this.taskData.priority = value;
    userExtAnalytics.log('task_settings_action', { action: 'priority' });
  },

  _updateStatus(value) {
    this.taskData.status = value;
    // this.taskData.progress = bindStatusAndProgressHelper.calc(value, null).progress;
    userExtAnalytics.log('task_settings_action', { action: 'status' });
  },

  _updateProgress(value) {
    let newValue = value;

    if (value > 1) {
      newValue = 1;
    }

    this.taskData.progress = newValue;
    // this.taskData.status = bindStatusAndProgressHelper.calc(null, newValue * 100).status;

    let type = '';

    switch (newValue) {
    case 0: type = 'open'; break;
    case 1: type = 'done'; break;
    default: type = 'in progress';
    }
    userExtAnalytics.log('task_status_changed', { type });
    userExtAnalytics.log('task_settings_action', { action: 'progress' });
  },

  _updateType(value) {
    this.taskData.type = value;

    if (this.taskData.type === constants.TASK_TYPES.milestone) {
      this.taskData.duration = 0;
      this.taskData.color = constants.DEFAULT_TYPE_COLORS.MILESTONE;
    } else {
      this.taskData.duration = 60;
      this.taskData.color = constants.DEFAULT_TYPE_COLORS.TASK;
    }

    this.taskData.estimation = 0;

    userExtAnalytics.log('task_settings_action', { action: 'type' });
  },

  async _updateStartDate(value) {
    this.taskData.start_date = value;

    userExtAnalytics.log('task_settings_action', { action: 'start_date' });
  },

  _updateEndDate(value) {
    this.taskData.end_date = value;

    userExtAnalytics.log('task_settings_action', { action: 'end_date' });
  },

  _updateDuration(value) {
    if (+this.taskData.duration === +value) {
      return;
    }

    this.taskData.duration = +value;

    if (!this.taskData.duration || this.taskData.duration < 1) {
      this.taskData.duration = 1;
    }

    userExtAnalytics.log('task_settings_action', { action: 'duration' });
  },

  _updateEstimation(value) {
    if (+this.taskData.estimation === +value) {
      return;
    }

    this.taskData.estimation = +value;

    userExtAnalytics.log('task_settings_action', { action: 'estimation' });
  },

  _updateDeadline({ value, updateType }) {
    this.taskData.deadline = value;
    userExtAnalytics.log('task_settings_action', { action: 'deadline', type: updateType });
  },

  _updateDescription({ newValue }) {
    this.taskData.note = newValue;
  },

  _isTaskChildOf(childId, parentId, tasksMap) {
    let itemId = childId;

    while (itemId && tasksMap[itemId]?.parent) {
      if (tasksMap[itemId].parent === parentId) {
        return true;
      }

      itemId = tasksMap[itemId].parent;
    }

    return false;
  },
};

export default TaskSettingsController;
